import { ChangeEvent, DataSourceMode, DataSourceModeChangeEvent, EditRowDecorator, Snackbar, TableRow, TableRowMode, Textbox, TableRowCreationEvent, DataSourceExecutionEvent, TableRowDisplayEvent, ClickEvent, TableRowBeforeSaveEvent, DataSourceAction } from "@mcleod/components";
import { AutogenLayoutFuelFundedVendorDefaults } from "./autogen/AutogenLayoutFuelFundedVendorDefaults";
import { Api, ModelRow, Navigation, RowUpdateEvent, SuccessFail } from "@mcleod/core";
import { FuelFundedVendor } from "./FuelFundedVendor";

export class FuelFundedVendorDefaults extends AutogenLayoutFuelFundedVendorDefaults {
    async onLoad() {
        this.tableFundedVendors.onEditLayoutLoaded = this.tableFundedVendorsOnEditLoaded();
        this.tableDirectVendors.onEditLayoutLoaded = this.tableDirectVendorsOnEditLoaded();
        this.addLayoutDataLoadListener(() => this.syncTableQuickInfoData());
    }

    /** This is an event handler for the afterExecution event of sourceFuelFundedVendorDefault. */
    sourceFuelFundedVendorDefaultAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) {
            this.syncTableQuickInfoData();
        }
    }

    sourceFuelFundedVendorAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode == DataSourceMode.ADD) {
            this.tabAccounts.visible =
            this.tabProducts.visible =
            this.tabChainCodes.visible =
            this.tabAdvancedSettings.visible =
            this.tabWireCodes.visible = false
        }
    }

    syncTableQuickInfoData() {
        this.setDefaultValues(this.tableFundedVendors.quickAddData, false);
        this.tableFundedVendors.quickAddRow.displayComponentValues();
        this.setDefaultValues(this.tableDirectVendors.quickAddData, true);
        this.tableDirectVendors.quickAddRow.displayComponentValues();
    }

    /**
     * Check for duplicate vendor codes when the Card Provider field changes
     */
    async textboxCardProviderOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const vendorId = this.textboxCardProvider?.getFirstLookupModelData()?.get("id");
            try {
                const response = await Api.post("lme/dispatch/check-duplicate-code", {
                    id: vendorId,
                    table: "fuel_funded_vendor_defaults",
                    alt_field_name_for_dup_check: "fuel_interface_id"
                });
                const isDuplicate = response?.data?.[0]?.is_duplicate;
                if (isDuplicate) {
                    Snackbar.showWarningSnackbar("The selected vendor has already been created.");
                    this.textboxCardProvider.text = "";
                    this.textboxCardProvider.focus();
                }
            } catch (error) {
                Snackbar.showWarningSnackbar("Error checking for duplicates.");
            }
        }
    }

    /**
     * Handler for AP Voucher changes in Check section
     */
    textboxCheckAPVoucherOnChange(event: ChangeEvent): void {
        this.setCheckFieldVisibility(event.newValue);
    }

    /**
     * Sets the visibility and required state of AP Division, AP Vendor, and GL Account for Check section
     * based on the Create AP Voucher value (Manual/Regular = visible/required)
     */
    private setCheckFieldVisibility(apVoucherValue: string) {
        const showFields = apVoucherValue === "Manual" || apVoucherValue === "Regular";
        this.textboxCheckAPDivisonCode.setProps({ visible: showFields, required: showFields });
        this.textboxCheckVendorCode.setProps({ visible: showFields, required: showFields });
        this.textboxCheckGLAccount.setProps({ visible: showFields, required: showFields });
    }

    /**
     * Handler for AP Voucher changes in Fuel section
     */
    textboxFuelAPVoucherOnChange(event: ChangeEvent): void {
        this.setFuelFieldVisibility(event.newValue);
    }

    textboxMasterAccountOnBlur(event: ChangeEvent): void {
        const vendorDescr = this.textboxCardProvider?.text || "";
        if (["EFS LLC"].includes(vendorDescr)) {
            const account = this.tableFundedVendors.quickAddRow.findComponentById("textboxMasterAccount") as Textbox;
            const subAccount = this.tableFundedVendors.quickAddRow.findComponentById("textboxCustomerId") as Textbox;
            subAccount.text = event.newValue || account.text;
        }
    }

    /**
     * Sets the visibility and required state of AP Division, AP Vendor, and GL Account
     * based on the Create AP Voucher value (Manual/Regular = visible/required)
     */
    private setFuelFieldVisibility(apVoucherValue: string) {
        const showFields = apVoucherValue === "Manual" || apVoucherValue === "Regular";
        this.textboxFuelAPDivisionCode.setProps({ visible: showFields, required: showFields });
        this.textboxFuelAPVendorCode.setProps({ visible: showFields, required: showFields });
        this.textboxFuelGLAccount.setProps({ visible: showFields, required: showFields });
    }

    // Listener for the switch to show/hide advanced communication options
    switchCommAdvOptionsOnChange(event: ChangeEvent) {
        this.panelAdvCommunication.visible = !!event.newValue;
        this.setCommFieldVisibility(this.textboxCommunication.selectedItem.displayValue);
    }

    /**
     * Handler for Restrict Tractor Hub Updates switch
     */
    switchHubUpdateOnChange(event: ChangeEvent) {
        const isRestricted = !!event.newValue;
        this.textboxFuelHubVariance.visible = isRestricted;
    }

    textboxCommunicationOnChange(event: ChangeEvent) {
        this.setCommFieldVisibility(event.newValue);
    }

    /**
     * Sets the visibility of fields based on the selected communication method and advanced toggle
     */
    private setCommFieldVisibility(commMethod: string) {
        this.textboxLogin.visible = commMethod != "Local";
        this.textboxPassword.visible = commMethod != "Local";
        this.textboxServerName.visible = commMethod != "Local";
        this.textboxPort.visible = commMethod != "Local";
        this.textboxDirectory.visible = commMethod == "Local" || commMethod !== "HttpsConnection";
        this.panelAdvancedOptions.visible = ["Internet-sftp", "Internet-ftp"].includes(commMethod);
        const isAdvEnabled = this.panelAdvancedOptions.visible && this.switchCommAdvOptions.checked;
        this.panelAdvCommunication.visible = isAdvEnabled;
        if (isAdvEnabled) {
            // Proxy always for FTP/SFTP, Mode/Type only for FTP
            this.textboxProxyServer.visible = true;
            this.textboxConnectionMode.visible = commMethod === "Internet-ftp";
            this.textboxConnectionType.visible = commMethod === "Internet-ftp";
        }
    }

    tableVendorOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow();
        const isDirectVendorTable = tableRow.table.id === this.tableDirectVendors.id;
        if (tableRow.mode == TableRowMode.QUICK_ADD) {
            if (this.activeRow) {
                this.setDefaultValues(tableRow.data, isDirectVendorTable);
            }
        }
    }

    setDefaultValues(data: ModelRow, isDirectVendor: boolean) {
        if (!data)
            return;
        data.setValuesAndLookupModelData(this.activeRow, false);
        if (isDirectVendor) {
            data.set("ap_voucher_type", this.activeRow.get("create_ap_voucher"));
            const fuelApDivLmData = this.activeRow.getLookupModelData("fuel_ap_div_id");
            data.setValueAndLookupModelData("ap_div_id", this.activeRow.get("fuel_ap_div_id"), fuelApDivLmData);
            const vendorLmData = this.activeRow.getLookupModelData("vendor_id");
            data.setValueAndLookupModelData("ap_vendor_id", this.activeRow.get("vendor_id"), vendorLmData);
        }
    }

    /** Funded Vendor Logic - START */

    tableFundedVendorsOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const fuelFundedVendor = rowDecorator.layout as FuelFundedVendor;
            const hasAdvancedData = fuelFundedVendor.hasAdvancedOptionsData();

            fuelFundedVendor.textboxAccountCode.enabled = tableRow.mode == TableRowMode.QUICK_ADD;
            fuelFundedVendor.textboxSubAccount.enabled = tableRow.mode == TableRowMode.QUICK_ADD;
            fuelFundedVendor.switchCommAdvOptions.checked = hasAdvancedData;
            // Remove the save and add another button. We can implement this later if needed.
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    tableFundedVendorsBeforeRowSave(event) {
        const modelRow: ModelRow = event.getTableRow().data;
        this.checkForDuplicateAccounts(modelRow, event);
    }

    private checkForDuplicateAccounts(modelRow: ModelRow<any>, event: any) {
        const accountCode = modelRow.get("master_account");
        const subAccount = modelRow.get("customer_id");

        // Check for vendor account duplicates in the datasource
        const isDuplicate = this.sourceFuelFundedVendor.data.some(row => row.get("master_account") === accountCode &&
            row.get("customer_id") === subAccount &&
            row !== modelRow // Exclude the current row being edited
        );

        if (isDuplicate) {
            event.preventDefault();
            Snackbar.showWarningSnackbar("Duplicate Account/Sub Account combination is not allowed.");
            const quickAddCustomerTextbox = this.tableFundedVendors.quickAddRow.findComponentById("textboxCustomerId") as Textbox;
            quickAddCustomerTextbox.clear();
            quickAddCustomerTextbox.focus();
        }
    }

    /** Funded Vendor Logic - END */

    /** Direct Vendor Logic - START */

    /** This is an event handler for the onRowDisplay event of tableDirectVendors. */
    tableDirectVendorsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const apVoucherValue = tableRow.data.get("ap_voucher_type");
        this.setVoucherComponentsEnabled(tableRow, apVoucherValue);
    }

    tableDirectVendorsOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            // Remove the save and add another button. We can implement this later if needed.
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    /** This is an event handler for the onChange event of textboxApVoucherType. */
    textboxApVoucherTypeOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        if (textbox.selectedItem != null)
            this.setVoucherComponentsEnabled(tableRow, textbox.selectedItem.value);
        else {
            textbox.selectedItem = textbox.items[0]; // Default to None
        }
    }

    setVoucherComponentsEnabled(tableRow: TableRow, apVoucherValue: string) {
        const allowEdit = apVoucherValue === "M" || apVoucherValue === "R";
        tableRow.findComponentById("textboxApDivId")?.setProps({ enabled: allowEdit, required: allowEdit });
        tableRow.findComponentById("textboxApVendorId")?.setProps({ enabled: allowEdit, required: allowEdit });
        tableRow.findComponentById("textboxApGlid")?.setProps({ enabled: allowEdit, required: allowEdit });
    }

    /** Direct Vendor Logic - END */

    /** This is an event handler for the onClick event of buttonGLControl. */
    buttonGLControlOnClick(event: ClickEvent) {
        Navigation.navigateTo("lme/integrations/fuel/FuelGLControlList", { newTab: true });
    }

    /** This is an event handler for the beforeRowSave event of tableFuelCodeProducts. */
    tableFuelCodeProductsBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("interface_id", this.activeRow.get("fuel_interface_id"));
        if (modelRow._appending && this.duplicateProductFound(modelRow.get("product_code"))) {
            const textboxProductCode = tableRow.findComponentById("textboxProductCode") as Textbox;
            textboxProductCode?.clear();
            Snackbar.showWarningSnackbar(`Duplicate Product [${modelRow.get("product_code")}] is not allowed.`);
        }
    }

    duplicateProductFound(productCode: string): boolean {
        return this.sourceFuelProdCode.data.some(row => row.get("product_code") === productCode);
    }

    /** This is an event handler for the beforeRowSave event of tableChainCodes. */
    tableChainCodesBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("interface_id", this.activeRow.get("fuel_interface_id"));
        if (modelRow._appending && this.duplicateChainCode(modelRow.get("code"))) {
            const textboxTableChainCode = tableRow.findComponentById("textboxTableChainCode") as Textbox;
            textboxTableChainCode?.clear();
            Snackbar.showWarningSnackbar(`Duplicate Chain Code [${modelRow.get("code")}] is not allowed.`);
        }
    }

    duplicateChainCode(chainCode: string): boolean {
        return this.sourceFuelChainCode.data.some(row => row.get("code") === chainCode);
    }

    /** This is an event handler for the beforeRowSave event of tableParsingClasses. */
    tableParsingClassesBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("interface_id", this.activeRow.get("fuel_interface_id"));
        if (modelRow._appending && this.duplicateParsingClass(modelRow.get("parse_class"))) {
            const textboxParseClass = tableRow.findComponentById("textboxParseClass") as Textbox;
            textboxParseClass?.clear();
            Snackbar.showWarningSnackbar(`Duplicate Parsing Class [${modelRow.get("parse_class")}] is not allowed.`);
        }
    }

    duplicateParsingClass(parsingClass: string): boolean {
        return this.sourceFuelFileList.data.some(row => row.get("parse_class") === parsingClass);
    }
}
